import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../auth/AuthProvider';
import type { SupabaseClient } from '../../lib/supabase';
import type { Task } from '../tasks/types';
import { dealKeys } from './hooks';
import type { Pipeline } from './types';

/**
 * Tasks that belong to a deal.
 *
 * These are ordinary rows in public.tasks with deal_id set and project_id
 * null, which is exactly why they turn up in the assignee's My Tasks without
 * anything extra being written for it.
 *
 * A deal task carries no section or status — it has no project to own one, and
 * the database refuses them. Its grouping on this view comes from the DEAL's
 * section, not from a section of its own.
 */

const DEAL_TASK_COLUMNS =
  'id, deal_id, lead_id, company_id, title, assignee_id, due_date, priority, is_completed, completed_at, position, created_at';

export interface DealTask {
  id: string;
  deal_id: string;
  /** Optional Outbound links, meaningful only on deal tasks. */
  lead_id: string | null;
  company_id: string | null;
  title: string;
  assignee_id: string | null;
  due_date: string | null;
  priority: Task['priority'];
  is_completed: boolean;
  completed_at: string | null;
  position: number;
  created_at: string;
}

export async function fetchDealTasks(
  client: SupabaseClient,
  dealIds: string[],
): Promise<DealTask[]> {
  if (dealIds.length === 0) {
    return [];
  }
  const { data, error } = await client
    .from('tasks')
    .select(DEAL_TASK_COLUMNS)
    .in('deal_id', dealIds)
    .order('position');
  if (error) {
    throw new Error(error.message);
  }
  return (data ?? []) as unknown as DealTask[];
}

export async function createDealTask(
  client: SupabaseClient,
  input: {
    dealId: string;
    title: string;
    assigneeId: string | null;
    dueDate: string | null;
    position: number;
    createdBy: string;
  },
): Promise<void> {
  // project_id is sent as an explicit null: the one-parent constraint is what
  // keeps a task from belonging to a deal and a project at once, so omitting
  // it would be a different statement than saying it has none.
  const { error } = await client.from('tasks').insert({
    deal_id: input.dealId,
    project_id: null,
    title: input.title.trim(),
    assignee_id: input.assigneeId,
    due_date: input.dueDate,
    position: input.position,
    created_by: input.createdBy,
  });
  if (error) {
    throw new Error(error.message);
  }
}

export function useDealTasks(pipeline: Pipeline, dealIds: string[]) {
  const { client } = useAuth();
  return useQuery({
    queryKey: [...dealKeys.all, 'tasks', pipeline, dealIds.length],
    queryFn: () => fetchDealTasks(client, dealIds),
    enabled: dealIds.length > 0,
    retry: false,
  });
}

export function useCreateDealTask() {
  const { client, access } = useAuth();
  const queryClient = useQueryClient();
  const createdBy = access.status === 'approved' ? access.profile.id : '';
  return useMutation({
    mutationFn: (input: {
      dealId: string;
      title: string;
      assigneeId: string | null;
      dueDate: string | null;
      position: number;
    }) => createDealTask(client, { ...input, createdBy }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: dealKeys.all });
      // It lands in the assignee's My Tasks too, so that list is stale now.
      void queryClient.invalidateQueries({ queryKey: ['my-tasks'] });
    },
  });
}

/**
 * Ticking a deal task off.
 *
 * Ced, 9 September: "permission denied for table tasks", every time, for
 * everyone. His guess was a missing DELETE grant or a role that needed admin
 * elevation. It was neither. DELETE on tasks is granted to `authenticated`
 * table-wide, and no deal task has ever had a delete path at all.
 *
 * What this used to send was is_completed AND completed_at AND completed_by.
 * The first is granted. The other two are not, and deliberately: 20260801000200
 * revoked everything on tasks and granted UPDATE on a named column list that
 * leaves them out, because `tasks_maintain_timestamps()` derives both from
 * is_completed and a CHECK refuses a pair that disagrees. Postgres reports a
 * column-privilege refusal as "permission denied for TABLE tasks", which is
 * what sent Ced looking at roles and deletes.
 *
 * Reproduced as the browser's own role against a database with every
 * migration applied: the three-column update errors, the one-column update
 * succeeds, and DELETE is allowed.
 *
 * So: send only is_completed, exactly as the project board does — tasks/hooks
 * has carried a comment saying why since it was written. The trigger stamps
 * the rest, which is the point. A client that can write completed_by can
 * claim somebody else finished the work.
 */
export function useSetDealTaskCompleted() {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { taskId: string; isCompleted: boolean }) => {
      const { error } = await client
        .from('tasks')
        .update({ is_completed: input.isCompleted })
        .eq('id', input.taskId);
      if (error) {
        throw new Error(error.message);
      }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: dealKeys.all });
      void queryClient.invalidateQueries({ queryKey: ['my-tasks'] });
    },
  });
}

/**
 * The columns a deal task can be edited on from the Tasks tab. Deliberately
 * the same five the row shows: what you can see is what you can change.
 */
export type DealTaskChanges = Partial<
  Pick<DealTask, 'title' | 'assignee_id' | 'due_date' | 'lead_id' | 'company_id'>
>;

/** An edit from the task form. Only the keys supplied are written. */
export function useUpdateDealTask() {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { taskId: string; changes: DealTaskChanges }) => {
      const { error } = await client.from('tasks').update(input.changes).eq('id', input.taskId);
      if (error) {
        throw new Error(error.message);
      }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: dealKeys.all });
      void queryClient.invalidateQueries({ queryKey: ['my-tasks'] });
    },
  });
}
